import { StyleProvider } from "@ant-design/cssinjs";
import { App as AntApp, theme as antdTheme, ConfigProvider } from "antd";
import { type ReactNode, useEffect, useMemo } from "react";
import { useThemeStore } from "@/shared/store/theme.store";

interface AntdProviderProps {
	children: ReactNode;
}

// Shared constants
const PRIMARY = "#059669";
const SUCCESS = "#16a34a";
const RADIUS = 8;

const lightTheme = {
	token: {
		colorPrimary: PRIMARY,
		colorSuccess: SUCCESS,
		borderRadius: RADIUS,
		colorBgLayout: "#f9fafb",
		colorBgContainer: "#ffffff",
		fontSize: 14,
	},
	components: {
		Layout: {
			siderBg: "transparent",
			headerBg: "#ffffff",
			headerPadding: "0 16px",
			bodyBg: "#f9fafb",
		},
		Menu: {
			darkItemBg: "transparent",
			darkSubMenuItemBg: "transparent",
			darkItemSelectedBg: "rgba(5, 150, 105, 0.15)",
			darkItemSelectedColor: "#ffffff",
			darkItemHoverBg: "rgba(255, 255, 255, 0.06)",
			darkItemHoverColor: "#ffffff",
			darkItemColor: "rgba(255, 255, 255, 0.65)",
			darkGroupTitleColor: "rgba(255, 255, 255, 0.35)",
			groupTitleFontSize: 11,
			itemBorderRadius: 6,
			itemMarginInline: 8,
			itemHeight: 40,
			iconSize: 16,
		},
		Card: {
			borderRadiusLG: RADIUS,
		},
		Table: {
			borderRadiusLG: RADIUS,
			headerBg: "#fafbfc",
		},
		Button: {
			borderRadius: RADIUS,
		},
		Modal: {
			borderRadiusLG: RADIUS,
		},
	},
};

const darkTheme = {
	token: {
		colorPrimary: PRIMARY,
		colorSuccess: SUCCESS,
		borderRadius: RADIUS,
		colorBgLayout: "#0f172a",
		colorBgContainer: "#1e293b",
		colorBgElevated: "#334155",
		fontSize: 14,
	},
	components: {
		Layout: {
			siderBg: "transparent",
			headerBg: "#1e293b",
			headerPadding: "0 16px",
			bodyBg: "#0f172a",
		},
		Menu: {
			darkItemBg: "transparent",
			darkSubMenuItemBg: "transparent",
			darkItemSelectedBg: "rgba(5, 150, 105, 0.2)",
			darkItemSelectedColor: "#ffffff",
			darkItemHoverBg: "rgba(255, 255, 255, 0.06)",
			darkItemHoverColor: "#ffffff",
			darkItemColor: "rgba(255, 255, 255, 0.65)",
			darkGroupTitleColor: "rgba(255, 255, 255, 0.35)",
			groupTitleFontSize: 11,
			itemBorderRadius: 6,
			itemMarginInline: 8,
			itemHeight: 40,
			iconSize: 16,
		},
		Card: {
			colorBgContainer: "#1e293b",
			borderRadiusLG: RADIUS,
		},
		Table: {
			colorBgContainer: "#1e293b",
			headerBg: "#1e293b",
			rowHoverBg: "#334155",
			borderRadiusLG: RADIUS,
		},
		Button: {
			borderRadius: RADIUS,
		},
		Modal: {
			borderRadiusLG: RADIUS,
			contentBg: "#1e293b",
			headerBg: "#1e293b",
		},
	},
};

export function AntdProvider({ children }: AntdProviderProps) {
	const { theme: mode, hydrate } = useThemeStore();

	useEffect(() => {
		hydrate();
		document.documentElement.setAttribute("data-theme", mode);
	}, [hydrate, mode]);

	const activeTheme = useMemo(() => (mode === "dark" ? darkTheme : lightTheme), [mode]);

	const algorithm = useMemo(
		() => (mode === "dark" ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm),
		[mode]
	);

	return (
		<StyleProvider layer>
			<ConfigProvider
				theme={{
					...activeTheme,
					algorithm,
				}}
			>
				<AntApp>{children}</AntApp>
			</ConfigProvider>
		</StyleProvider>
	);
}
